import React from 'react';

import { CurrentUser } from '@qualio/ui-components';
import { Link, Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { DateTime } from 'luxon';

import { aslRootStyle } from '../ASLExportDocument';
import PageCounter from './PageCounter';

type CoverPageProps = {
  currentUser: CurrentUser;
};

const coverPageStyle = StyleSheet.create({
  header: {
    paddingLeft: 22,
    paddingTop: 220,
  },
  companyName: {
    fontSize: 12,
  },
  pageTitle: {
    fontSize: 32,
    lineHeight: '100%',
    marginBottom: 24,
    fontWeight: 700,
  },
  createdAt: {
    fontSize: 10,
    marginBottom: 12,
  },
  bodyText: {
    fontSize: 8,
  },
  logo: {
    marginTop: 4,
    marginBottom: 4,
  },
  generatedBy: {
    fontSize: 10,
    marginBottom: 8,
  },
  footer: {
    paddingRight: 64,
    position: 'absolute',
    fontSize: 12,
    bottom: 48,
    left: 64,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  link: {
    fontSize: 8,
    color: '#000000',
    textDecoration: 'none',
  },
  pageCounterContainer: {
    fontSize: 8,
    alignSelf: 'flex-end',
  },
});

const CoverPage: React.FC<CoverPageProps> = ({ currentUser }) => {
  return (
    <Page size="A4" style={aslRootStyle.page}>
      <View style={coverPageStyle.header}>
        <Text style={coverPageStyle.companyName}>{currentUser.companyName}</Text>
        <Text style={coverPageStyle.pageTitle}>Approved Supplier List</Text>
        <Text style={coverPageStyle.generatedBy}>Generated by {currentUser.fullName}</Text>
        <Text style={coverPageStyle.createdAt}>{DateTime.utc().toFormat('yyyy-LL-dd HH:mm (ZZZZ)')}</Text>
      </View>
      <View style={coverPageStyle.footer}>
        <View>
          <Text style={coverPageStyle.bodyText}>Generated with</Text>
          <Svg width="67" height="20" viewBox="0 0 67 20" style={coverPageStyle.logo}>
            <Path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.2591 16.0996C13.2453 15.62 11.9156 14.9154 11.1428 14.4972L11.0456 14.4441C8.11646 12.8439 4.8818 11.077 0.86228 11.5423C0.762336 11.557 0.660416 11.5506 0.563095 11.5235C0.465775 11.4965 0.375215 11.4493 0.297258 11.385C0.2193 11.3208 0.155677 11.2409 0.110492 11.1505C0.0653076 11.0602 0.0395648 10.9613 0.0349248 10.8604C-0.0266035 10.1028 -0.0067915 9.34084 0.0940215 8.58748C0.285082 7.24924 0.745459 5.96363 1.44734 4.80832C2.14922 3.65301 3.07803 2.65198 4.17765 1.86572C5.27727 1.07945 6.52488 0.524278 7.84509 0.233732C9.1653 -0.0568134 10.5307 -0.0766973 11.8588 0.175282C14.319 0.650506 16.5127 2.02831 18.009 4.03813C19.5053 6.04796 20.1962 8.54458 19.946 11.0377C19.8414 12.1983 19.5191 13.3287 18.9959 14.3699C18.587 15.2182 17.8633 15.8736 16.9787 16.1966C16.0941 16.5196 15.1184 16.4848 14.2591 16.0996ZM11.8588 13.197C12.6589 13.6516 13.8795 14.2994 14.8705 14.7495C15.3777 14.9793 15.9551 15.0004 16.4777 14.8082C17.0004 14.6159 17.4265 14.2258 17.6639 13.7221C18.2239 12.5698 18.5168 11.3061 18.5208 10.025C18.5248 8.74385 18.2398 7.47833 17.687 6.3226C17.1342 5.16687 16.3279 4.15069 15.328 3.34971C14.3282 2.54874 13.1605 1.98358 11.912 1.69631C10.6635 1.40905 9.36632 1.40707 8.11694 1.69052C6.86757 1.97398 5.69821 2.53557 4.69591 3.3335C3.69362 4.13142 2.88419 5.14513 2.32788 6.29917C1.77157 7.45321 1.48271 8.71786 1.4828 9.99898C1.59148 9.99898 1.70949 9.99659 1.83776 9.994C3.32168 9.96396 6.17875 9.90614 11.8588 13.197ZM8.59937 19.9023C6.88983 19.6689 5.27079 18.9934 3.90229 17.9426C2.53378 16.8918 1.46319 15.5021 0.796322 13.9108C0.752186 13.8028 0.733908 13.686 0.742949 13.5698C0.75199 13.4535 0.7881 13.341 0.848393 13.2411C0.908686 13.1413 0.991491 13.057 1.09019 12.9948C1.18889 12.9327 1.30075 12.8946 1.41684 12.8834C4.76944 12.6129 7.55381 14.1176 10.4814 15.7178C11.7156 16.3996 13.1657 17.1633 14.3295 17.6452C14.4619 17.6992 14.5759 17.7904 14.6575 17.9079C14.7391 18.0254 14.7848 18.164 14.7892 18.307C14.7935 18.4499 14.7562 18.5911 14.6819 18.7133C14.6076 18.8355 14.4993 18.9335 14.3704 18.9954C12.5794 19.8638 10.5704 20.1795 8.59937 19.9023ZM2.67151 14.3449C5.58316 19.0567 10.4609 18.734 12.1088 18.2566C11.3065 17.8566 10.5314 17.4338 9.76994 17.0179L9.76593 17.0157C7.39443 15.7209 5.13627 14.488 2.67151 14.3449ZM53.0198 14.0903C52.9243 13.9948 52.7447 13.7721 52.7447 13.0743V4.50527C52.2638 4.42647 51.7732 4.42647 51.2923 4.50527V13.3948C51.2923 14.4687 51.7151 14.8545 51.8986 15.0218L51.9015 15.0245C52.6493 15.7064 54.2176 15.4314 54.4017 15.3495C54.4054 14.9665 54.3603 14.5847 54.2676 14.213C54.1903 14.2426 53.3448 14.4085 53.0198 14.0903ZM43.9597 7.92827C44.2961 7.81917 47.0669 7.05091 48.492 8.26012C49.0125 8.70108 49.2625 9.42842 49.2625 10.4172V15.1654L49.2153 15.1764C48.69 15.3003 45.6189 16.0246 44.1893 14.9722C43.9188 14.7721 43.3369 14.2789 43.3369 13.1061C43.3271 12.7316 43.4099 12.3606 43.578 12.0259C43.7462 11.6912 43.9944 11.4033 44.3007 11.1877C45.4519 10.3105 47.4516 10.5803 47.8002 10.6273L47.826 10.6308V10.3512C47.826 9.76709 47.6806 9.3625 47.3828 9.13521C46.4463 8.43969 44.528 9.00111 44.2416 9.08975C44.0463 8.73443 43.9491 8.33357 43.9597 7.92827ZM45.3735 14.1653C45.8645 14.4721 47.3078 14.3494 47.826 14.2084V11.7946C46.4827 11.6105 44.8053 11.5991 44.8053 13.0833C44.8053 13.8094 45.1793 14.0436 45.3501 14.1505L45.3735 14.1653ZM40.198 14.1494V7.76468H40.1912C40.6713 7.68589 41.1611 7.68589 41.6413 7.76468V15.1268C40.7545 15.3851 39.8373 15.525 38.9138 15.5427C37.3045 15.5427 36.7408 15.1586 36.4135 14.904C36.0862 14.6495 35.3611 14.0176 35.3611 11.9856V7.76468C35.6008 7.72491 35.8433 7.70514 36.0862 7.70558C36.3344 7.70527 36.5822 7.72504 36.8272 7.76468V11.9265C36.8272 13.2084 37.1909 13.6607 37.3682 13.838C37.5455 14.0153 38.2342 14.5858 40.198 14.1494ZM59.2476 11.5718C59.2476 9.04429 60.6273 7.58733 62.7889 7.58733C64.9573 7.58733 66.3301 9.05338 66.3301 11.5718C66.3301 14.0902 64.9505 15.5427 62.7889 15.5427C60.6273 15.5427 59.2476 14.0993 59.2476 11.5718ZM60.8046 11.5718C60.8046 12.4514 60.9251 14.3721 62.7889 14.3721C64.6527 14.3721 64.7891 12.4514 64.7891 11.5718C64.7891 10.6922 64.6527 8.78745 62.7889 8.78745C60.9251 8.78745 60.8046 10.6922 60.8046 11.5718ZM56.7518 4.54391C56.4863 4.5439 56.2212 4.56365 55.9586 4.60301C55.9232 4.85058 55.9087 5.10072 55.9155 5.35073C55.909 5.60074 55.9235 5.85085 55.9586 6.09846C56.2211 6.1389 56.4863 6.15942 56.7518 6.15982C57.0174 6.15986 57.2826 6.13935 57.545 6.09846C57.5801 5.85085 57.5946 5.60074 57.5882 5.35073C57.5949 5.10072 57.5805 4.85058 57.545 4.60301C57.2825 4.56319 57.0173 4.54344 56.7518 4.54391ZM56.0337 15.3632V7.76468C56.5093 7.68587 56.9946 7.68587 57.4702 7.76468V15.3632C56.9948 15.445 56.509 15.445 56.0337 15.3632ZM31.4267 14.9426C33.5451 13.6516 33.5451 10.6695 33.5451 10.0467C33.5451 9.2466 33.5451 4.53476 29.056 4.53476C24.5669 4.53476 24.5533 9.2466 24.5533 10.0467C24.5533 10.8468 24.5533 15.5427 29.056 15.5427C29.71 15.5427 30.2702 15.8667 30.871 16.2142C31.8759 16.7953 32.9946 17.4423 34.8566 16.7496C34.7815 16.287 34.6096 15.8455 34.352 15.454C33.1652 15.93 32.4294 15.512 31.5912 15.0358L31.5905 15.0354C31.5364 15.0046 31.4818 14.9736 31.4267 14.9426ZM29.0742 14.238H29.056C26.1671 14.238 26.1671 10.6967 26.1671 10.0467C26.1671 9.39661 26.1671 5.86899 29.056 5.86899C31.9449 5.86899 31.9449 9.40343 31.9449 10.0467V10.0542C31.945 10.717 31.9452 14.2222 29.0742 14.238Z"
              fill="#3D4CF2"
            />
          </Svg>
          <Text style={coverPageStyle.bodyText}>QMS for Life Sciences</Text>
          <Link style={coverPageStyle.link} src="https://www.qualio.com">
            www.qualio.com
          </Link>
        </View>
        <View style={coverPageStyle.pageCounterContainer}>
          <PageCounter />
        </View>
      </View>
    </Page>
  );
};

export default CoverPage;
